import React from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";

/**
 * Um filtro de escolha múltipla para os relatórios.
 *
 * ─── PORQUE EXISTE ──────────────────────────────────────────────────────────
 *
 * Os filtros dos relatórios eram todos de valor único: um médico, um criador,
 * um estado. Quem queria ver o trabalho de três secretárias tirava três
 * relatórios e somava à mão — e somar à mão é onde aparecem os números que não
 * batem.
 *
 * Fica aqui, em `relatorios/components/ui`, ao lado do `FiltrosRelatorio`,
 * porque os dois relatórios de pesquisa precisam exatamente do mesmo controlo
 * e um terceiro vai precisar também.
 *
 * ─── A CONTAGEM AO LADO DE CADA OPÇÃO ───────────────────────────────────────
 *
 * Quando a opção traz `total`, aparece quantos registos tem. É o que evita a
 * pergunta seguinte: escolheu-se um médico, vieram zero linhas, e não se sabia
 * se o filtro estava errado ou se ele não tinha mesmo trabalho no período.
 */
function MultiSelectRelatorio({
  label,
  value = [],
  onChange,
  opcoes = [],
  // Como ler cada opção. Por omissão serve a forma `{_id, name, total}`.
  chaveDe = (o) => o._id ?? o.valor ?? o,
  rotuloDe = (o) => o.name ?? o.nome ?? o.valor ?? String(o),
  totalDe = (o) => o.total,
  largura = 240,
  size = "small",
  disabled = false,
}) {
  const rotuloPorChave = new Map(
    opcoes.map((o) => [String(chaveDe(o)), rotuloDe(o)]),
  );

  return (
    <FormControl sx={{ width: largura }} size={size} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(e) => onChange(e.target.value)}
        input={<OutlinedInput label={label} />}
        renderValue={(escolhidos) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {escolhidos.map((v) => (
              <Chip
                key={v}
                size="small"
                label={rotuloPorChave.get(String(v)) || String(v)}
              />
            ))}
          </Box>
        )}
        MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
      >
        {opcoes.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="textSecondary">
              Sem opções nos dados do período
            </Typography>
          </MenuItem>
        )}

        {opcoes.map((o) => {
          const chave = chaveDe(o);
          const total = totalDe(o);
          return (
            <MenuItem key={chave} value={chave} dense>
              <Checkbox
                size="small"
                checked={value.some((v) => String(v) === String(chave))}
              />
              <ListItemText
                primary={rotuloDe(o)}
                /* Quem já saiu da clínica continua a aparecer: o relatório olha
                   para trás e o trabalho dele está lá. Mas diz-se, para não
                   parecer que ainda cá está. */
                secondary={
                  [
                    total !== undefined ? `${total} registo(s)` : null,
                    o.ativo === false ? "inativo" : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || undefined
                }
              />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

export default MultiSelectRelatorio;
